import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Badge } from "@/components/ui/badge";
import {
  Upload, FileSpreadsheet, Trash2, CheckCircle2, AlertTriangle, ArrowRight,
  Container as ContainerIcon, Scale, Boxes, Ruler, History, Loader2,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const authH = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem("token")}` } });
const fmt = (n) => (n == null || n === "—" ? "—" : Number(n).toLocaleString(undefined, { maximumFractionDigits: 0 }));

const Delta = ({ value, unit = "", goodWhenNegative = true }) => {
  if (value == null || value === 0) return <span className="text-slate-400 text-xs">no change</span>;
  const good = goodWhenNegative ? value < 0 : value > 0;
  return (
    <span className={`text-xs font-medium ${good ? "text-emerald-600" : "text-amber-600"}`}>
      {value > 0 ? "+" : ""}{fmt(value)}{unit}
    </span>
  );
};

const Stat = ({ icon: Icon, label, manual, auto, delta, unit = "" }) => (
  <div className="rounded-lg border border-slate-200 p-3" data-testid={`compare-stat-${label.toLowerCase().replace(/\s+/g, "-")}`}>
    <div className="flex items-center gap-1.5 text-xs text-slate-500 mb-2"><Icon className="w-3.5 h-3.5" />{label}</div>
    <div className="flex items-end justify-between gap-2">
      <div><div className="text-[10px] uppercase tracking-wide text-slate-400">Manual</div><div className="text-lg font-semibold text-slate-800">{fmt(manual)}<span className="text-xs text-slate-400 ml-0.5">{unit}</span></div></div>
      <ArrowRight className="w-4 h-4 text-slate-300 mb-1.5" />
      <div className="text-right"><div className="text-[10px] uppercase tracking-wide text-blue-400">Auto</div><div className="text-lg font-semibold text-blue-700">{fmt(auto)}<span className="text-xs text-blue-300 ml-0.5">{unit}</span></div></div>
    </div>
    {delta}
  </div>
);

export default function ManualCompareDialog({ open, onOpenChange, typeId, poId, poNumber }) {
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [history, setHistory] = useState([]);

  const loadHistory = useCallback(async () => {
    if (!open) return;
    try {
      const params = {};
      if (poId) params.po_id = poId;
      else if (poNumber) params.po_number = poNumber;
      const r = await axios.get(`${API}/container-planner/comparisons`, { ...authH(), params });
      setHistory(r.data.comparisons || []);
    } catch { setHistory([]); }
  }, [open, poId, poNumber]);

  useEffect(() => { loadHistory(); }, [loadHistory]);
  useEffect(() => { if (!open) { setResult(null); setFile(null); } }, [open]);

  const runCompare = async () => {
    if (!file) { toast.error("Choose a manual stuffing-plan Excel first"); return; }
    setLoading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const params = { save: true, ignore_layer_cap: true };
      if (typeId) params.container_type_id = typeId;
      if (poId) params.po_id = poId;
      if (poNumber) params.po_number = poNumber;
      const r = await axios.post(`${API}/container-planner/compare`, fd, {
        ...authH(), params, headers: { ...authH().headers, "Content-Type": "multipart/form-data" },
      });
      setResult(r.data);
      toast.success("Comparison ready");
      loadHistory();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not compare the plan");
    } finally { setLoading(false); }
  };

  const openSaved = async (id) => {
    try {
      const r = await axios.get(`${API}/container-planner/comparisons/${id}`, authH());
      setResult(r.data.comparison);
    } catch { toast.error("Could not load that comparison"); }
  };

  const deleteSaved = async (id, e) => {
    e.stopPropagation();
    try {
      await axios.delete(`${API}/container-planner/comparisons/${id}`, authH());
      loadHistory();
      toast.success("Deleted");
    } catch { toast.error("Delete failed"); }
  };

  const c = result;
  const tone = c?.diff?.verdict_tone;
  const toneCls = tone === "good" ? "border-emerald-300 bg-emerald-50 text-emerald-800"
    : tone === "warn" ? "border-amber-300 bg-amber-50 text-amber-800"
    : "border-blue-300 bg-blue-50 text-blue-800";

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-5xl max-h-[92vh] p-0 overflow-hidden" data-testid="manual-compare-dialog">
        <DialogHeader className="px-5 pt-5 pb-3 border-b border-slate-200">
          <DialogTitle className="flex items-center gap-2 text-slate-900"><FileSpreadsheet className="w-5 h-5 text-emerald-600" />Compare Manual vs Auto Plan</DialogTitle>
          <DialogDescription>Upload your hand-made stuffing plan; the ERP re-plans the same goods automatically and shows the difference.</DialogDescription>
        </DialogHeader>

        <ScrollArea className="max-h-[calc(92vh-88px)]">
          <div className="p-5 space-y-5">
            {/* Upload row */}
            <div className="flex flex-wrap items-center gap-3 rounded-lg border border-dashed border-slate-300 bg-slate-50 p-3">
              <input id="manual-plan-file" type="file" accept=".xlsx,.xlsm" className="hidden"
                onChange={(e) => { setFile(e.target.files?.[0] || null); setResult(null); }} data-testid="manual-compare-file-input" />
              <label htmlFor="manual-plan-file" className="inline-flex items-center gap-2 cursor-pointer rounded-md border border-slate-300 bg-white px-3 py-2 text-sm hover:bg-slate-100" data-testid="manual-compare-choose-btn">
                <Upload className="w-4 h-4" />{file ? file.name : "Choose manual plan (.xlsx)"}
              </label>
              <Button onClick={runCompare} disabled={loading || !file} size="sm" className="bg-emerald-600 hover:bg-emerald-700" data-testid="manual-compare-run-btn">
                {loading ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <FileSpreadsheet className="w-4 h-4 mr-1" />}{loading ? "Comparing…" : "Compare"}
              </Button>
              {poNumber && <span className="text-xs text-slate-500">PO {poNumber}</span>}
            </div>

            {/* History */}
            {history.length > 0 && !c && (
              <div data-testid="compare-history">
                <div className="flex items-center gap-1.5 text-xs font-medium text-slate-500 mb-2"><History className="w-3.5 h-3.5" />Past comparisons</div>
                <div className="space-y-1.5">
                  {history.map((h) => (
                    <div key={h.id} onClick={() => openSaved(h.id)} className="flex items-center justify-between rounded-md border border-slate-200 px-3 py-2 text-sm hover:bg-slate-50 cursor-pointer" data-testid={`compare-history-item-${h.id}`}>
                      <div className="min-w-0"><div className="truncate text-slate-700">{h.filename}</div><div className="text-[11px] text-slate-400">{new Date(h.created_at).toLocaleString()} · {h.comparison?.manual?.container_count} vs {h.comparison?.auto?.container_count} containers</div></div>
                      <button onClick={(e) => deleteSaved(h.id, e)} className="text-slate-400 hover:text-red-500 p-1" data-testid={`compare-history-delete-${h.id}`}><Trash2 className="w-4 h-4" /></button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {c && (
              <div className="space-y-5" data-testid="compare-result">
                {/* Verdict */}
                <div className={`rounded-lg border px-4 py-3 flex items-start gap-2 ${toneCls}`} data-testid="compare-verdict">
                  {tone === "good" ? <CheckCircle2 className="w-5 h-5 mt-0.5" /> : tone === "warn" ? <AlertTriangle className="w-5 h-5 mt-0.5" /> : <ContainerIcon className="w-5 h-5 mt-0.5" />}
                  <div>
                    <div className="text-sm font-medium">{c.diff?.verdict}</div>
                    {c.diff?.pallet_note && <div className="text-xs mt-0.5 opacity-80">{c.diff.pallet_note}</div>}
                    <div className="text-[11px] mt-1 opacity-70">Container type: {c.container_type?.name} · cap {fmt(c.container_type?.max_payload_kg)} kg. Weights compared as GROSS (incl. pallet tare).</div>
                  </div>
                </div>

                {c.unmatched?.length > 0 && (
                  <div className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800" data-testid="compare-unmatched">
                    {c.unmatched.length} code(s) not found in the catalog (skipped from the auto plan): {c.unmatched.join(", ")}
                  </div>
                )}

                {/* Scorecard */}
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
                  <Stat icon={ContainerIcon} label="Containers" manual={c.manual?.container_count} auto={c.auto?.container_count} delta={<Delta value={c.diff?.container_count_delta} />} />
                  <Stat icon={Scale} label="Total weight" manual={c.manual?.total_gross_weight_kg} auto={c.auto?.total_weight_kg} unit="kg" delta={<Delta value={c.diff?.weight_delta_kg} unit="kg" goodWhenNegative={false} />} />
                  <Stat icon={Boxes} label="Pallets" manual={c.manual?.total_pallets} auto={c.auto?.total_pallets} delta={<Delta value={c.diff?.pallet_delta} />} />
                  <Stat icon={Boxes} label="Pieces" manual={"—"} auto={c.auto?.total_pieces} delta={<span className="text-[11px] text-slate-400">auto derived</span>} />
                </div>

                {/* Per-container */}
                <div>
                  <div className="text-sm font-medium text-slate-700 mb-2">Per-container breakdown</div>
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
                    <div>
                      <div className="text-[11px] uppercase tracking-wide text-slate-400 mb-1.5">Manual plan</div>
                      <div className="space-y-2">
                        {c.manual?.containers?.map((mc, i) => (
                          <div key={i} className="rounded-lg border border-slate-200 p-2.5" data-testid={`manual-container-${i}`}>
                            <div className="text-xs font-medium text-slate-700 mb-1">{mc.label}</div>
                            <div className="flex flex-wrap gap-x-4 gap-y-1 text-[11px] text-slate-500">
                              <span className="inline-flex items-center gap-1"><Scale className="w-3 h-3" />{fmt(mc.gross_weight_kg)} kg</span>
                              <span className="inline-flex items-center gap-1"><Boxes className="w-3 h-3" />{mc.pallets} pallets</span>
                              <span className="inline-flex items-center gap-1"><Ruler className="w-3 h-3" />{fmt(mc.used_length_in)}/{fmt(mc.internal_length_in)} in</span>
                              <span>L/R {mc.lane_balance?.left_pct}/{mc.lane_balance?.right_pct}%</span>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                    <div>
                      <div className="text-[11px] uppercase tracking-wide text-blue-400 mb-1.5">Auto plan</div>
                      <div className="space-y-2">
                        {c.auto?.containers?.map((ac, i) => (
                          <div key={i} className="rounded-lg border border-blue-200 bg-blue-50/40 p-2.5" data-testid={`auto-container-${i}`}>
                            <div className="flex items-center justify-between mb-1">
                              <div className="text-xs font-medium text-slate-700">{ac.label}</div>
                              <div className="flex items-center gap-1">
                                {ac.quality_score != null && <Badge variant="outline" className="text-[10px] h-4 px-1 border-slate-300">Q{ac.quality_score}</Badge>}
                                <Badge className={`text-[10px] h-4 px-1 ${ac.status === "fits" ? "bg-emerald-100 text-emerald-700" : ac.status === "over_loaded" ? "bg-red-100 text-red-700" : "bg-amber-100 text-amber-700"}`}>{ac.status}</Badge>
                              </div>
                            </div>
                            <div className="flex flex-wrap gap-x-4 gap-y-1 text-[11px] text-slate-500">
                              <span className="inline-flex items-center gap-1"><Scale className="w-3 h-3" />{fmt(ac.weight_kg)} kg</span>
                              <span className="inline-flex items-center gap-1"><Boxes className="w-3 h-3" />{ac.pallets} pallets</span>
                              <span className="inline-flex items-center gap-1"><Ruler className="w-3 h-3" />{fmt(ac.used_length_in)}/{fmt(ac.internal_length_in)} in</span>
                              <span>{fmt(ac.utilisation_pct)}% full</span>
                              <span>L/R {ac.lane_balance?.left_pct}/{ac.lane_balance?.right_pct}%</span>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Product placement diff */}
                <div>
                  <div className="text-sm font-medium text-slate-700 mb-2">Product placement</div>
                  <div className="rounded-lg border border-slate-200 overflow-hidden">
                    <table className="w-full text-xs" data-testid="compare-product-table">
                      <thead className="bg-slate-50 text-slate-500">
                        <tr>
                          <th className="text-left font-medium px-3 py-2">Product</th>
                          <th className="text-right font-medium px-3 py-2">Manual pallets</th>
                          <th className="text-right font-medium px-3 py-2">Auto pallets</th>
                          <th className="text-center font-medium px-3 py-2">Manual container(s)</th>
                          <th className="text-center font-medium px-3 py-2">Auto container(s)</th>
                        </tr>
                      </thead>
                      <tbody>
                        {c.diff?.product_diffs?.map((pd, i) => {
                          const diff = !pd.pallets_match || !pd.placement_match;
                          return (
                            <tr key={i} className={`border-t border-slate-100 ${diff ? "bg-amber-50/60" : ""}`} data-testid={`compare-product-row-${pd.code}`}>
                              <td className="px-3 py-1.5"><span className="font-medium text-slate-700">{pd.code}</span>{pd.description ? <span className="text-slate-400"> · {pd.description}</span> : null}</td>
                              <td className="px-3 py-1.5 text-right">{pd.manual_pallets}</td>
                              <td className={`px-3 py-1.5 text-right ${!pd.pallets_match ? "text-amber-700 font-medium" : ""}`}>{pd.auto_pallets}</td>
                              <td className="px-3 py-1.5 text-center text-slate-500">{pd.manual_containers?.join(", ") || "—"}</td>
                              <td className={`px-3 py-1.5 text-center ${!pd.placement_match ? "text-amber-700 font-medium" : "text-slate-500"}`}>{pd.auto_containers?.join(", ") || "—"}</td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                  <div className="text-[11px] text-slate-400 mt-1.5">Highlighted rows: the auto-planner grouped this product into different containers or a different pallet count than your manual plan.</div>
                </div>
              </div>
            )}
          </div>
        </ScrollArea>
      </DialogContent>
    </Dialog>
  );
}
